import { useMemo } from 'react';
import { useCalendarStatus } from './useCalendar';
import { useOutlookCalendarStatus } from './useOutlook';

/**
 * useScheduleCapabilities — canonical source of "can this user schedule a
 * meeting?" for the Contact Details Schedule flow (issue #503).
 *
 * Composes the two per-provider calendar hooks (there is no aggregator hook):
 *   - Google Calendar  → useCalendarStatus()        (/api/oauth/calendar/status)
 *   - Outlook Calendar → useOutlookCalendarStatus() (/api/oauth/outlook/status,
 *                                                     connected && has_calendar_scope)
 *
 * Do NOT read useCalendarStatus().outlook — it is a hardcoded 'disconnected'
 * stub (hooks/useCalendar.js). Outlook's live signal is useOutlookCalendarStatus,
 * which requires BOTH the OAuth connection AND the calendar scope (issue #484
 * semantics: a mailbox-only Outlook grant must not unlock scheduling).
 */
export const SCHEDULE_DISABLED_COPY =
    'Connect Google Calendar or Outlook in Settings → Integrations to schedule meetings';

export function useScheduleCapabilities() {
    const { data: calStatus, isLoading: googleLoading } = useCalendarStatus();
    const { data: outlookCal, isLoading: outlookLoading } = useOutlookCalendarStatus();

    const googleConnected = !!calStatus?.google?.connected;   // /oauth/calendar/status
    const outlookConnected = !!outlookCal?.connected;         // connected && has_calendar_scope
    const anyCalendarConnected = googleConnected || outlookConnected;

    // Drives CalendarPicker's 0 / 1 / many contract. `sendable` is always true
    // here by construction: a calendar only appears once its connection (and,
    // for Outlook, its calendar scope) is live — there is no "connected but
    // not sendable" calendar state to express.
    //
    // Memoised on the two booleans (#514 audit A20): consumers list `calendars`
    // in effect dependencies (the default-selection effects in
    // MeetingActionModal and CalendarEditorPage), and a fresh array per
    // render re-ran them on every keystroke. No loop — the functional setState
    // bails when the selection is still valid — but wasted work.
    const calendars = useMemo(() => [
        ...(googleConnected
            ? [{ value: 'google', label: 'Google Calendar', provider: 'google', sendable: true }]
            : []),
        ...(outlookConnected
            ? [{ value: 'outlook', label: 'Outlook Calendar', provider: 'outlook', sendable: true }]
            : []),
    ], [googleConnected, outlookConnected]);

    return {
        googleConnected,
        outlookConnected,
        anyCalendarConnected,
        calendars,
        isLoading: googleLoading || outlookLoading,
    };
}